import { Button, Input, Radio, RadioChangeEvent, notification } from 'antd';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { FormItemsWrapper, OtherSurgeriesWrapper, StyledForm } from './style';
import { useState } from 'react';
import { AddMoreBtnContainer } from '../../Common/Button/style';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useUpdateProfessionalClientSurgeries } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientSurgeries } from '../../../hooks/client/useClientGeneralInfo';
import { theme } from '../../../styles/theme';
import { CheckCircleOutlined } from '@ant-design/icons';
import { NotificationTitle } from '../../Common/Notification/style';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';

const SurgeriesForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && 'professionalCode' in user;

  const [form] = StyledForm.useForm();
  const { update: updateProfessionalClientSurgeries, loading: loadingProfessional } = useUpdateProfessionalClientSurgeries();
  const { update: updateClientSurgeries, loading: loadingClient } = useUpdateClientSurgeries();
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);

  const loading = isClient ? loadingClient : loadingProfessional;
  const [withSurgeries, setWithSurgeries] = useState<boolean>(Boolean(selectedClient?.surgeries?.length) ?? false);

  const onFinish = async (values: any) => {
    const paramsToSend = (values.surgeries || [])
      .filter((item: any) => !item._destroy)
      .map((item: any) => {
        // id is ID! in the schema.
        // For existing DB records pass the real id (update).
        // For new records, we send an empty string '' because it's a non-nullable field.
        // The backend should interpret an empty string as "not an existing record".
        const idValue = (item.id != null && item.id !== '' && String(item.id) !== '0') ? String(item.id) : '';

        return {
          id: idValue,
          forWhat: item.forWhat,
          when: item.when,
        };
      });


    try {
      let res;
      if (isClient) {
        res = await updateClientSurgeries(paramsToSend);
      } else {
        res = await updateProfessionalClientSurgeries(selectedClient?.id as string, paramsToSend);
      }

      const success = isClient ? res?.clientUpdateSurgeries : res?.professionalClientUpdateSurgeries;

      if (success) {
        await refetchClient();
        api.info({
          message: <NotificationTitle>Surgeries updated successfully</NotificationTitle>,
          placement: 'topRight',
          icon: <CheckCircleOutlined style={{ color: theme.color.primary }} />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex',
          },
        });
      }
    } catch (error) {
      console.error('Error saving surgeries:', error);
      api.error({
        message: <NotificationTitle>Error saving surgeries</NotificationTitle>,
        placement: 'topRight',
        style: {
          background: `${theme.bg.secondary}`,
          borderRadius: 10,
          alignItems: 'center',
          display: 'flex',
        },
      });
    }
  };

  const onChangeWithSurgeries = ({ target: { value } }: RadioChangeEvent) => {
    setWithSurgeries(value);
  };

  const onRemoveSurgery = (fieldIndex: any) => {
    const oldSurgeries = form.getFieldValue('surgeries') || [];
    const newSurgeries = oldSurgeries.map((item: any, index: number) =>
      index === fieldIndex ? { ...item, _destroy: true } : item
    );
    form.setFieldsValue({ surgeries: newSurgeries });
  };

  return (
    <>
      {contextHolder}
      <StyledForm
        {..._FORM_ITEM_LAYOUT}
        form={form}
        name="surgeries_form"
        onFinish={onFinish}
        initialValues={{ surgeries: selectedClient?.surgeries }}
        style={{ marginTop: '1rem' }}
        scrollToFirstError
      >
        <p>Have you had any surgeries?</p>
        <Radio.Group
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onChange={onChangeWithSurgeries}
          value={withSurgeries}
          optionType="button"
          buttonStyle="solid"
        />
        {withSurgeries && (
          <FormItemsWrapper>
            <StyledForm.List name="surgeries">
              {(fields, { add, remove }) => (
                <>
                  {fields
                    .filter(({ name }) => {
                      const surgeries = form.getFieldValue('surgeries') || [];
                      const fieldData = surgeries[name];
                      return !fieldData?._destroy;
                    })
                    .map((field: any, index: number) => (
                      <OtherSurgeriesWrapper key={field.key}>
                        <div className="number-container">
                          <p>{`${++index})`}</p>
                          <Button
                            size="small"
                            onClick={() => {
                              onRemoveSurgery(field.name);
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                        <StyledForm.Item
                          name={[field.name, 'forWhat']}
                          label="For what?"
                          labelAlign="left"
                          rules={[
                            {
                              required: true,
                              message: 'This is a required field!',
                            },
                          ]}
                        >
                          <Input />
                        </StyledForm.Item>
                        <StyledForm.Item
                          name={[field.name, 'when']}
                          label="When?"
                          labelAlign="left"
                          rules={[
                            {
                              required: true,
                              message: 'This is a required field!',
                            },
                          ]}
                        >
                          <Input />
                        </StyledForm.Item>
                        <StyledForm.Item name={[field.name, 'id']} hidden>
                          <Input />
                        </StyledForm.Item>
                      </OtherSurgeriesWrapper>
                    ))}
                  <AddMoreBtnContainer>
                    <Button
                      size="small"
                      onClick={() => add()}
                    >
                      Add more
                    </Button>
                  </AddMoreBtnContainer>
                </>
              )}
            </StyledForm.List>
          </FormItemsWrapper>
        )}
        <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
          <Button loading={loading} type="primary" htmlType="submit">
            Save
          </Button>
        </StyledForm.Item>
      </StyledForm>
    </>
  )
}

export default SurgeriesForm;
